import { useEffect, useState } from 'react'
import { http } from '../api/client'
import { Endpoints } from '../api/endpoints'
import type { CurrentPlan } from '../types'
import { Badge } from './ui/badge'
import { Skeleton } from './ui/skeleton'
import { Link } from 'react-router-dom'
import { AlertCircle, Zap, CheckCircle, XCircle, X } from 'lucide-react'

export function CurrentPlanBadge() {
  const [plan, setPlan] = useState<CurrentPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [dismissedBadge, setDismissedBadge] = useState(false)

  useEffect(() => {
    async function loadCurrentPlan() {
      try {
        const data = await http.get<CurrentPlan>(Endpoints.plans.current)
        setPlan(data)
      } catch (err: any) {
        if (err.response?.status === 404) {
          setPlan(null)
        }
      } finally {
        setLoading(false)
      }
    }
    loadCurrentPlan()
  }, [])

  if (loading) {
    return <Skeleton className="h-12 w-full rounded-lg" />
  }

  if (dismissedBadge || !plan) {
    return (
      <Link to="/plans" className="block">
        <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20 p-3 hover:bg-muted/40 transition-colors">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="w-4 h-4" />
            <span>No active plan</span>
          </div>
        </div>
      </Link>
    )
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Active':
        return { icon: CheckCircle, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-500/10 border-green-500/20' }
      case 'InTrial':
        return { icon: Zap, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' }
      case 'Expired':
      case 'TrialEnded':
        return { icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10 border-red-500/20' }
      default:
        return { icon: AlertCircle, color: 'text-muted-foreground', bg: 'bg-muted/20' }
    }
  }

  const config = getStatusConfig(plan.status)
  const Icon = config.icon

  return (
    <Link to="/current-plan" className="block">
      <div className={`rounded-lg border p-3 hover:shadow-sm transition-all ${config.bg}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold">{plan.planName}</span>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        {plan.status === 'InTrial' && plan.daysRemaining !== null && (
          <div className="flex items-center justify-between gap-1">
            <div className={`text-xs ${config.color} font-medium`}>
              {plan.daysRemaining} day{plan.daysRemaining !== 1 ? 's' : ''} left in trial
            </div>
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDismissedBadge(true)
              }}
              className={`${config.color} hover:opacity-70 transition-opacity`}
              aria-label="Dismiss"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        {plan.status === 'Active' && (
          <div className="text-xs text-muted-foreground">
            Active subscription
          </div>
        )}
        {(plan.status === 'Expired' || plan.status === 'TrialEnded') && (
          <div className="flex items-center justify-between gap-1">
            <div className={`text-xs ${config.color} font-medium`}>
              Please upgrade
            </div>
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDismissedBadge(true)
              }}
              className={`${config.color} hover:opacity-70 transition-opacity`}
              aria-label="Dismiss"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <div className="mt-2 flex gap-1">
          {plan.hasFamilyAccess && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">Family</Badge>
          )}
          {plan.hasAiCoach && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">AI</Badge>
          )}
          {plan.hasAdvancedAnalytics && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">Analytics</Badge>
          )}
        </div>
      </div>
    </Link>
  )
}
